import React, { useEffect, useMemo, useState } from 'react'
import { trans } from 'sharedKernel/i18n'
import { ScoutingPdfFileIcon } from 'domain/feature/hub/company/scouting/VisualizationScreen/components/ScoutingAbout/ScoutingPdfFileIcon'
import {
  formatOpportunityVerticalsDisplay,
  isExternalUrl,
  normalizeExternalUrl,
} from 'domain/feature/hub/company/scouting/scoutingOpportunityDisplay'
import { DEFAULT_OPPORTUNITY_IMAGE } from '../../../constants'
import type { IOpportunitySupportFile, IStartupOpportunityDetail } from '../../../contracts'
import {
  displayOrDash,
  getOpportunitySupportFileDisplayName,
  getResolverOpportunityCoverImage,
  getResolverOpportunitySupportFiles,
} from '../../../behavior'
import {
  AboutRoot,
  CoverBanner,
  CoverBannerWrapper,
  MetaBar,
  MetaItem,
  MetaLabel,
  MetaValue,
  DescriptionCard,
  DescriptionHeader,
  DescriptionTitle,
  DescriptionDivider,
  DescriptionBody,
  DetailBlock,
  DetailLabel,
  DetailValue,
  DetailLink,
  SupportFilesList,
  SupportFileChip,
} from 'domain/feature/hub/company/scouting/VisualizationScreen/components/ScoutingAbout/styles'
import {
  OpportunityDetailAboutRoot,
  OpportunityDescriptionCard,
  OpportunityDescriptionHeader,
} from '../../style'

interface OpportunityDetailAboutProps {
  opportunity: IStartupOpportunityDetail
  fileRows?: IOpportunitySupportFile[]
  showCover?: boolean
  showMeta?: boolean
  compactBelowMeta?: boolean
}

const getSupportFiles = (
  fileRows: IOpportunitySupportFile[] | undefined,
  opportunity: IStartupOpportunityDetail
): IOpportunitySupportFile[] => getResolverOpportunitySupportFiles(fileRows, opportunity)

export const OpportunityDetailAbout = ({
  opportunity,
  fileRows,
  showCover = true,
  showMeta = true,
  compactBelowMeta = false,
}: OpportunityDetailAboutProps): JSX.Element => {
  const Root = compactBelowMeta ? OpportunityDetailAboutRoot : AboutRoot
  const Card = compactBelowMeta ? OpportunityDescriptionCard : DescriptionCard
  const Header = compactBelowMeta ? OpportunityDescriptionHeader : DescriptionHeader
  const defaultCoverSrc = useMemo(
    () => getResolverOpportunityCoverImage(opportunity, fileRows),
    [opportunity, fileRows]
  )
  const [coverSrc, setCoverSrc] = useState(defaultCoverSrc)

  useEffect(() => {
    setCoverSrc(defaultCoverSrc)
  }, [defaultCoverSrc])

  const supportFiles = getSupportFiles(fileRows, opportunity)
  const descriptionHtml = opportunity.description?.trim()
  const supportLink = opportunity.supportLink?.trim() ?? ''

  const renderSupportLink = (): JSX.Element => {
    if (!supportLink) {
      return <DetailValue>{displayOrDash()}</DetailValue>
    }

    if (!isExternalUrl(supportLink)) {
      return <DetailLink as="span">{supportLink}</DetailLink>
    }

    return (
      <DetailLink href={normalizeExternalUrl(supportLink)} target="_blank" rel="noopener noreferrer">
        {supportLink}
      </DetailLink>
    )
  }

  const renderSupportMaterials = (): JSX.Element => {
    if (supportFiles.length === 0) {
      return <DetailValue>{displayOrDash()}</DetailValue>
    }

    return (
      <SupportFilesList>
        {supportFiles.map((file, index) => {
          const displayName =
            getOpportunitySupportFileDisplayName(file, opportunity.title) ||
            trans('scouting.about.unnamedFile')

          return (
            <SupportFileChip
              key={file.id ?? `file-${index}`}
              href={file.path ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              download={displayName}
            >
              <ScoutingPdfFileIcon />
              <span>{displayName}</span>
            </SupportFileChip>
          )
        })}
      </SupportFilesList>
    )
  }

  return (
    <Root style={compactBelowMeta ? undefined : { width: '100%', maxWidth: '100%' }}>
      {showCover && (
        <CoverBannerWrapper>
          <CoverBanner
            src={coverSrc}
            alt={opportunity.title || trans('scouting.about.coverAlt')}
            onError={() => setCoverSrc(DEFAULT_OPPORTUNITY_IMAGE)}
          />
        </CoverBannerWrapper>
      )}

      {showMeta && (
        <MetaBar>
          <MetaItem>
            <MetaLabel>{trans('scouting.about.offeringInstitution')}:</MetaLabel>
            <MetaValue>{displayOrDash(opportunity.requesterName)}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{trans('scouting.about.segment')}:</MetaLabel>
            <MetaValue>{displayOrDash(formatOpportunityVerticalsDisplay(opportunity) || null)}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>{trans('scouting.about.impacted')}:</MetaLabel>
            <MetaValue>{displayOrDash(opportunity.impactedBy)}</MetaValue>
          </MetaItem>
        </MetaBar>
      )}

      <Card>
        <Header>
          <DescriptionTitle>{trans('scouting.about.descriptionTitle')}</DescriptionTitle>
          <DescriptionDivider />
        </Header>

        {descriptionHtml ? (
          <DescriptionBody dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
        ) : (
          <DescriptionBody>{trans('scouting.about.descriptionNotProvided')}</DescriptionBody>
        )}

        <DetailBlock>
          <DetailLabel>{trans('scouting.about.solutionPaths')}:</DetailLabel>
          <DetailValue>{displayOrDash(opportunity.references)}</DetailValue>
        </DetailBlock>
        <DetailBlock>
          <DetailLabel>{trans('scouting.about.supportLink')}:</DetailLabel>
          {renderSupportLink()}
        </DetailBlock>
        <DetailBlock>
          <DetailLabel>{trans('scouting.about.supportMaterials')}</DetailLabel>
          {renderSupportMaterials()}
        </DetailBlock>
      </Card>
    </Root>
  )
}
